import { message } from "antd";
import { getGetAttivitaQueryKey } from "client/api/backend/attivita/attivita";
import type {
  AttivitaDTO,
  BEResponseListFirmaResponseDTO,
  Certificato,
  FirmaRequest,
} from "client/api/backend/schemas";
import { queryClient } from "client/core/network/queryClient";
import { groupBy } from "lodash";
import { useCallback } from "react";
import type {
  ProviderParameters,
  RemoteSignType,
} from "../../provider-parameters/ProviderParameters";
import type { IRemoteSignErrorBag } from "../../reducer/RemoteSignActions";
import { RemoteSignStep } from "../../reducer/RemoteSignReducer";
import { MassiveRemoteSignLogic } from "../../utils/MassiveRemoteSignLogic";
import { useRemoteSignApi } from "./useRemoteSignApi";

interface IParams {
  provider: ProviderParameters | null | undefined;
  signType: RemoteSignType | null | undefined;
  certificate: Certificato | null | undefined;
  tasks: AttivitaDTO[];
  setCurrentStep: (step: RemoteSignStep) => void;
  setErrors: (
    errorBag: IRemoteSignErrorBag,
  ) => ((fn: (bag: IRemoteSignErrorBag) => IRemoteSignErrorBag) => void) | void;
  flCompletare?: boolean;
}

/**
 * Hook per la gestione della firma remota di più documenti (azione massiva)
 */
export function useMassiveRemoteSign(params: IParams) {
  const {
    tasks,
    provider,
    signType,
    certificate,
    flCompletare,
    setCurrentStep,
    setErrors,
  } = params;

  const remoteSignFn = useRemoteSignApi<
    BEResponseListFirmaResponseDTO,
    FirmaRequest
  >({
    method: "POST",
    fields: provider?.remoteSignParameters ?? [],
    apiUrl: `/firma-remota/firma/${provider?.webserviceId}`,
    skip: !provider,
  });

  const handleSign = useCallback(
    async (
      values: Record<string, any>,
      requestBody: FirmaRequest,
      extraHeaders?: Record<string, string>,
    ): Promise<IRemoteSignErrorBag> => {
      const result = await remoteSignFn(values, requestBody, extraHeaders);

      // Se ci sono errori, li raccolgo in un array così da mostrarli
      const resultWithError = result?.data?.filter(
        (response) => response.error,
      );

      const grouped = groupBy(resultWithError, "idTaskInstance");

      const errorBag: IRemoteSignErrorBag = Object.keys(grouped).map(
        (taskId) => ({
          taskId,
          messages: grouped[taskId].map(
            (response) => response.errorMessage ?? "Errore sconosciuto",
          ),
        }),
      );

      return errorBag;
    },
    [remoteSignFn],
  );

  /**
   * Deve richiamare la funzione di firma remota per ogni documento dentro i task
   */
  const handleSubmit = useCallback(
    async (values: Record<string, any>) => {
      try {
        // Pre-computo tutte le request per conoscere il totale dei documenti
        const allRequests: FirmaRequest[] = [];
        for (const task of tasks) {
          const documentsRequests =
            await MassiveRemoteSignLogic.getRemoteSignBodyData({
              taskId: task.idTaskInstance,
              flCompletare: flCompletare ?? false,
              certificate: certificate!,
              signType: signType!,
            });
          allRequests.push(...documentsRequests);
        }

        const totalDocuments = allRequests.length;
        const errorBag: IRemoteSignErrorBag = [];

        // Faccio la firma remota per ogni documento
        for (let i = 0; i < allRequests.length; i++) {
          const signResult = await handleSign(values, allRequests[i], {
            totalDocuments: String(totalDocuments),
            currentIndex: String(i),
          });
          if (signResult.length > 0) {
            errorBag.push(...signResult);
          }
        }
        setErrors(errorBag);
        await queryClient.invalidateQueries(getGetAttivitaQueryKey());
        setCurrentStep(RemoteSignStep.Signing);
      } catch (e: any) {
        console.error(`[RemoteSignCertificateStep] Si è verificato un errore durante il processo di firma`, { error: e }); // prettier-ignore
        const errorMessage = e?.response?.data?.esito?.errore.messaggioDiErrore ?? e.message; // prettier-ignore
        message.error(`Si è verificato un errore durante il processo di firma: ${errorMessage}`); // prettier-ignore
        return;
      }
    },
    [
      tasks,
      setCurrentStep,
      setErrors,
      certificate,
      signType,
      flCompletare,
      handleSign,
    ],
  );

  return handleSubmit;
}
